import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { INadaVehicleRequest } from '@app/models/nada-vehicle-request.model';
import {
  INadaVehicleByVin,
  INadaVehicleByVinResponse,
  INadaVehicleResponse,
  IVehicleValueReport
} from '@app/models/nada-vehicle-response.model';
import {
  INadaVehicleValueRequest,
  INadaVehicleValueResponse
} from '@app/models/nada-vehicle-value.model';
import { CollateralList } from '@app/models/Other-Collateral';
import {
  IValidateMotorcycleVin,
  IValidateVin,
  VehicleMakesResponse,
  VehicleMMS,
  VehicleModelsResponse,
  VehicleStylesResponse,
  VinLookupRequest,
  VinLookupResponse
} from '@app/models/vehicle.model';
import {
  IVehicleDescOptionCodes,
  IVehicleOptions
} from '@app/models/vehicle-options.model';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Constants } from 'src/app/constants';
import { Cacheable } from 'ts-cacheable';
import { NotificationService } from './notification.service';
import { vehicleCacheBuster$ } from '@app/shared/utils/cache-buster-utility';

@Injectable({
  providedIn: 'root'
})
export class VinLookupService {
  cachedVinDetailsMap$: Map<string, Observable<IValidateVin>> = new Map();
  private cachedVehicleYearsRegular: Array<string> | null = null;
  private cachedVehicleYearsClassics: Array<string> | null = null;
  private fetchingVehicleYearsRegular$: Observable<Array<string>> | null = null;
  private fetchingVehicleYearsClassics$: Observable<Array<string>> | null =
    null;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private notificationService: NotificationService
  ) { }

  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public validate(vin: string): Observable<IValidateVin> {
    return this.http
      .get<IValidateVin>(`${Constants.appConfiguration.vinApiRoot}/vin/${vin}`)
      .pipe(
        catchError(error => {
          this.logger.error('Error fetching VIN data: ', error);
          return of(null);
        }),
        shareReplay(1)
      );
  }

  /**
   * Validate motorcycle vin
   * @param { string } vin
   */
  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public validateMotorcycle(vin: string): Observable<IValidateMotorcycleVin> {
    return this.http
      .get<IValidateMotorcycleVin>(
        `${Constants.appConfiguration.vinApiRoot}/vin/GetVinDetailFromVinLink/${vin}`
      )
      .pipe(shareReplay());
  }

  /**
   * Gets Other Collateral List
   * @param { portalAccountId }
   * @returns Other Collateral List
   */
  @Cacheable()
  public getOtherCollateralList(
    portalAccountId: number
  ): Observable<CollateralList[]> {
    return this.http.get<CollateralList[]>(
      `${Constants.appConfiguration.apiRoot}/Vehicle/OtherCollaterals/${portalAccountId}`
    );
  }

  /**
   * Gets the years values for a vehicle
   *
   * Method also sets a local cache of the responses received for each param type
   * Furthermore, in Express, due to a race condition between requests from the Vehicle modal and Vehicle component,
   * the caching isn't effective (simultaneous calls). So, the fetching observable is also cached to just pass back
   * the requesting components the obs instead of doing an additional HTTP call.
   *
   * @param isIncludeClassics {boolean} - query to indicate if values need to be older than 1981
   * @returns Observable Array of Strings
   */
  public getVehicleYears(
    isIncludeClassics?: boolean,
    isOtherCollateralSelected?: boolean,
    isPowerSportsWarranty: boolean = false
  ): Observable<Array<string>> {
    let targetCache: Array<string> | null;
    let targetFetchingObservable: Observable<Array<string>> | null;
    let setFetchingObservable: (obs: Observable<Array<string>> | null) => void;

    if (isIncludeClassics) {
      targetCache = this.cachedVehicleYearsClassics;
      targetFetchingObservable = this.fetchingVehicleYearsClassics$;
      setFetchingObservable = obs => (this.fetchingVehicleYearsClassics$ = obs);
    } else {
      targetCache = this.cachedVehicleYearsRegular;
      targetFetchingObservable = this.fetchingVehicleYearsRegular$;
      setFetchingObservable = obs => (this.fetchingVehicleYearsRegular$ = obs);
    }

    if (targetCache) {
      return of(targetCache);
    }
    if (targetFetchingObservable) {
      return targetFetchingObservable;
    }
    const includeClassicsQuery = isIncludeClassics
      ? '&includeClassics=true'
      : '';

    const vehicleYearsApiRequest$ = this.http
      .get<
        string[]
      >(`${Constants.appConfiguration.vinApiRoot}/vin/VehicleYears?isPowerSportsWarranty=${isPowerSportsWarranty && isOtherCollateralSelected}&isCollateralSelected=${isOtherCollateralSelected}${includeClassicsQuery}`)
      .pipe(
        tap(() => {
          setFetchingObservable(null);
        }),
        catchError(error => {
          setFetchingObservable(null);
          const errorMessage = 'Error fetching vehicle years.';
          this.notificationService.showError(errorMessage);
          console.error(
            errorMessage + ` Include Classics: ${isIncludeClassics}`,
            error
          );
          return throwError(() => new Error(errorMessage));
        }),
        shareReplay(1)
      );
    setFetchingObservable(vehicleYearsApiRequest$);
    return vehicleYearsApiRequest$;
  }

  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public getOtherGetYears(
    isPowerSportsWarranty: boolean,
    isCollateralSelected: boolean
  ): Observable<Array<string>> {
    return this.http
      .get<Array<string>>(
        `${Constants.appConfiguration.vinApiRoot}/vin/OtherGetYears?isPowerSportsWarranty=${isPowerSportsWarranty}&isCollateralSelected=${isCollateralSelected}
      `
      )
      .pipe(shareReplay());
  }

  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public getVehicleMakesBasedOnYear(
    year,
    isPowerSportsWarranty: boolean = false
  ): Observable<VehicleMMS[]> {
    const url = `${Constants.appConfiguration.vinApiRoot}/vin/Makes/${year}?isPowerSportsWarranty=${isPowerSportsWarranty}`;
    return this.http.get<VehicleMakesResponse>(url).pipe(
      map(response => response.makes || []),
      shareReplay()
    );
  }

  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public getModelsBasedOnMake(
    year: number,
    make: { code: string; description: string },
    isPowerSportsWarranty: boolean = false
  ): Observable<VehicleMMS[]> {
    const url = `${Constants.appConfiguration.vinApiRoot}/vin/Models/${year}/${encodeURIComponent(make.code)}`;
    const params = new HttpParams()
      .set('makeDescription', make.description)
      .set('isPowerSportsWarranty', isPowerSportsWarranty);

    return this.http.get<VehicleModelsResponse>(url, { params }).pipe(
      map(response => response.models || []),
      shareReplay()
    );
  }

  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public getStyleOnModel(
    year,
    make,
    model,
    isPowerSportsWarranty: boolean = false
  ): Observable<VehicleMMS[]> {
    const url =
      `${Constants.appConfiguration.vinApiRoot}/vin/Styles/${year}/` +
      encodeURIComponent(make.code) +
      `/` +
      encodeURIComponent(model.code);
    const params = new HttpParams()
      .set('makeDescription', make.description)
      .set('modelDescription', model.description)
      .set('isPowerSportsWarranty', isPowerSportsWarranty);
    return this.http.get<VehicleStylesResponse>(url, { params }).pipe(
      map(response => response.styles || []),
      shareReplay()
    );
  }

  /**
   * Gets NADA Years List
   * @returns string[]
   */
  getNadaVehicleYears(): Observable<Array<string>> {
    return this.http
      .get<Array<string>>(`${Constants.appConfiguration.vinApiRoot}/nada/Years`)
      .pipe(shareReplay());
  }

  /**
   * Gets NADA Make List based on year
   * @param year {number}
   * @returns string[]
   */
  public getNadaMakesBasedOnYear(year: number): Observable<Array<string>> {
    return this.http
      .post<Array<string>>(
        `${Constants.appConfiguration.vinApiRoot}/nada/Makes`,
        {
          modelYear: year
        }
      )
      .pipe(shareReplay());
  }

  /**
   * Gets NADA Model List based on year and make
   * @param year {number}
   * @param make {string}
   * @returns string[]
   */
  public getNadaModelsBasedOnMake(
    year: number,
    make: string
  ): Observable<Array<string>> {
    return this.http
      .post<Array<string>>(
        `${Constants.appConfiguration.vinApiRoot}/nada/Series`,
        {
          modelYear: year,
          make
        }
      )
      .pipe(shareReplay());
  }

  /**
   * Gets NADA Style List based on year, make and model
   * @param year {number}
   * @param make {string}
   * @param model {string}
   * @returns string[]
   */
  public getNadaStyleOnModel(
    year: number,
    make: string,
    model: string
  ): Observable<Array<string>> {
    return this.http
      .post<Array<string>>(
        `${Constants.appConfiguration.vinApiRoot}/nada/BodyStyles`,
        {
          modelYear: year,
          make,
          model
        }
      )
      .pipe(shareReplay());
  }

  /**
   * Gets the list of NADA vehicles/trims for a given VIN.
   *
   * The response is a list of body style/trim options where `ucgVehicleId` is
   * the ID passed to the subsequent valuation call and `bodyStyleTrim` is the
   * display label.
   *
   * @param NadaVehicle {INadaVehicleRequest}
   * @returns INadaVehicleByVin[]
   */
  public getNadaVehiclesByVin(
    NadaVehicle: INadaVehicleRequest
  ): Observable<INadaVehicleByVin[]> {
    // 'nada-error-whitelist' is the only header that opts a 500 out of the
    // fatal error-page redirect; catchError then degrades failures to [] so
    // the UI hides the Style/Trim dropdown and values with a null ucgVehicleId.
    const headers = new HttpHeaders().set('nada-error-whitelist', 'true');
    return this.http
      .post<
        INadaVehicleByVinResponse[]
      >(`${Constants.appConfiguration.vinApiRoot}/nada/VehiclesByVin`, NadaVehicle, { headers })
      .pipe(
        map((response: INadaVehicleByVinResponse[]) =>
          (response || []).map(item => ({
            ucgVehicleId: item.key,
            bodyStyleTrim: item.value
          }))
        ),
        catchError(error => {
          this.logger.error(
            'getNadaVehiclesByVin() failed; hiding Body Style/Trim and valuing with null ucgVehicleId.',
            error
          );
          return of<INadaVehicleByVin[]>([]);
        }),
        shareReplay()
      );
  }

  /**
   * Gets Vehicle options
   * @param options {IVehicleOptions}
   * @returns IVehicleDescOptionCodes[]
   */
  @Cacheable({ cacheBusterObserver: vehicleCacheBuster$ })
  public getVehicleOptions(
    options: IVehicleOptions
  ): Observable<IVehicleDescOptionCodes[]> {
    return this.http
      .post<
        IVehicleDescOptionCodes[]
      >(`${Constants.appConfiguration.apiRoot}/Vehicle`, options)
      .pipe(shareReplay());
  }

  @Cacheable()
  public getVinLookup(
    request: VinLookupRequest
  ): Observable<VinLookupResponse> {
    const url = `${Constants.appConfiguration.vinApiRoot}/vin/GetVinLookup`;

    return this.http.post<VinLookupResponse>(url, request);
  }

  /**
   * Gets Vehicle details for vehicle valuation
   * @param vehicle {INadaVehicleRequest}
   * @returns INadaVehicleResponse
   */
  public getVehicleDetailsForNADA(
    vehicle: INadaVehicleRequest
  ): Observable<INadaVehicleResponse> {
    return this.http.post<INadaVehicleResponse>(
      `${Constants.appConfiguration.vinApiRoot}/nada/VehicleDetails`,
      vehicle
    );
  }

  /**
   * Gets Vehicle value for vehicle valuation
   * @param NadaVehicle {INadaVehicleValueRequest}
   * @returns INadaVehicleValueResponse
   */
  public getVehicleValueForNADA(
    NadaVehicle: INadaVehicleValueRequest
  ): Observable<INadaVehicleValueResponse> {
    return this.http.post<INadaVehicleValueResponse>(
      `${Constants.appConfiguration.vinApiRoot}/nada/VehicleValue`,
      NadaVehicle
    );
  }

  /**
   * Get Vehicle Value Report for vehicle valuation
   * @returns IVehicleValueReport data
   */
  public getVehicleValuationDetails(
    NadaVehicle: INadaVehicleValueRequest
  ): Observable<IVehicleValueReport> {
    return this.http.post<IVehicleValueReport>(
      `${Constants.appConfiguration.vinApiRoot}/nada/VehicleValueReport`,
      NadaVehicle
    );
  }
}
